import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { arbitrum } from 'viem/chains'

import { SUSDS_POINTS_MULTIPLIER } from '../../../consts'
import { USDC_ACCOUNT_ENABLED } from '../../flags'
import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { skySavingsGeneralStatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/skySavings'
import { skySavingsBackedByOptions } from '@/features/savings/data/savings-charts/backed-by-query/skySavingsBackedByQuery'
import {
  susdcSavingsRateQueryOptions,
  susdsSavingsRateQueryOptions,
} from '@/features/savings/data/savings-charts/savings-rate-query/skySavings'
import { ssrAuthOracleConverterQueryOptions } from '@/features/savings/logic/savings-converters/ssrAuthOracleConverter'

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD',
  decimals: 6,
  address: CheckedAddress('0xaf88d065e77c8cC2239327C5EDb3A432268e5831'),
  logo: tokenLogos.usdc,
})

const usds = new TokenDefinition({
  symbol: TokenSymbol('USDS'),
  name: 'USDS Stablecoin',
  decimals: 18,
  address: CheckedAddress('0x6491c05A82219b8D1479057361ff1654749b876b'),
  logo: tokenLogos.usds,
})

const susds = new TokenDefinition({
  symbol: TokenSymbol('sUSDS'),
  name: 'Savings USDS',
  decimals: 18,
  address: CheckedAddress('0xdDb46999F8891663a8F2828d25298f70416d7610'),
  logo: tokenLogos.susds,
})

const susdc = new TokenDefinition({
  symbol: TokenSymbol('sUSDC'),
  name: 'Savings USDC',
  decimals: 18,
  address: CheckedAddress('0x940098b108fB7D0a7E374f6eDED7760787464609'),
  logo: tokenLogos.susdc,
})

export const arbitrumConfig: ChainConfigEntry = {
  originChainId: arbitrum.id,
  meta: {
    name: 'Arbitrum',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([usdc, usds, susds, susdc]),
  nativeAssetInfo: {
    nativeAssetName: 'Ether',
    wrappedNativeAssetSymbol: TokenSymbol('WETH'),
    wrappedNativeAssetAddress: CheckedAddress('0x4200000000000000000000000000000000000006'),
    nativeAssetSymbol: TokenSymbol('ETH'),
    minRemainingNativeAssetBalance: NormalizedNumber(0.0005),
    nativeAssetLogo: tokenLogos.eth,
  },
  tokensWithMalformedApprove: [],
  tokensRequiringResetApproval: [],
  contractsRequiringUsdtResetApproval: [],
  permitSupport: {},
  sparkLend: undefined,
  legacyFarms: undefined,
  savings: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
      ...(USDC_ACCOUNT_ENABLED ? { [susdc.symbol]: { type: 'vault' as const, assetsDecimals: 6 } } : {}),
    },
    accounts: sortSavingsAccounts([
      ...(USDC_ACCOUNT_ENABLED
        ? [
            {
              savingsToken: susdc.symbol,
              underlyingToken: usdc.symbol,
              supportedAssets: [usdc.symbol],
              fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
              savingsRateQueryOptions: susdcSavingsRateQueryOptions,
              stats: {
                queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susdc.symbol),
                items: ['tvl', 'users', 'liquidity-sky-psm'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: skySavingsBackedByOptions,
              liquidityChartQueryOptions: undefined,
              sparkPointsMultiplier: undefined,
            },
          ]
        : []),
      {
        savingsToken: susds.symbol,
        underlyingToken: usds.symbol,
        supportedAssets: [usds.symbol, usdc.symbol],
        fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
        savingsRateQueryOptions: susdsSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susds.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: SUSDS_POINTS_MULTIPLIER,
      },
    ]),
    psmStables: [usds.symbol, usdc.symbol],
  },
  spk: undefined,
  points: undefined,
  sparkRewards: undefined,
  swap: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
    },
  },
}
